import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getDesignReviewRequests } from "../../api/design";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";
import { FileText, Calendar, User, Tag, AlertCircle, Plus, Search } from "lucide-react";

export default function DesignReviewList() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { id: projectId } = useParams();

  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError("");
        const res = await getDesignReviewRequests({
          page,
          page_size: pageSize,
        });
        setRows(res.data.results || []);
        setTotal(res.data.count || 0);
      } catch (err) {
        setError("데이터를 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [page, pageSize]);

  // 상태 스타일
  const STATUS_STYLES = {
    pending: "bg-gray-100 text-gray-700",
    in_progress: "bg-blue-100 text-blue-700",
    completed: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    approved: "bg-green-100 text-green-700",
  };

  // 페이지네이션 처리
  const pagedRows = Array.isArray(rows) ? rows : [];

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-3 border min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900 truncate">도면 검토 요청</h2>
              <p className="text-xs text-gray-500 mt-0.5">전체 {total}건</p>
            </div>
            <button
              onClick={() => navigate(`new`)}
              className="btn-create flex-shrink-0"
            >
              등록
            </button>
          </div>
        </div>

        {/* 검토 요청 리스트 */}
        <div className="space-y-2 min-w-0">
          {loading ? (
            <>
              <div className="bg-white rounded-lg p-4 border animate-pulse h-32" />
              <div className="bg-white rounded-lg p-4 border animate-pulse h-32" />
              <div className="bg-white rounded-lg p-4 border animate-pulse h-32" />
            </>
          ) : error ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : pagedRows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <p className="text-sm text-gray-500">검토 요청이 없습니다.</p>
              <button
                onClick={() => navigate(`new`)}
                className="btn-create mt-4"
              >
                새 요청 등록
              </button>
            </div>
          ) : (
            pagedRows.map((row) => (
              <button
                key={row.dns || row.id}
                onClick={() => navigate(`${row.dns}`)}
                className="w-full text-left bg-white rounded-lg p-2 border border-gray-200 hover:border-blue-300 active:bg-gray-50 transition-all overflow-hidden"
              >
                {/* 상단: 상태 + 카테고리/공종 */}
                <div className="flex items-center gap-1 mb-1">
                  <span
                    className={`px-1 py-px rounded text-[9px] font-medium flex-shrink-0 ${STATUS_STYLES[row.status] || "bg-gray-100 text-gray-600"}`}
                  >
                    {row.status_display || row.status || "대기"}
                  </span>
                  <span className="text-[9px] text-gray-500 font-medium px-1 py-px bg-gray-100 rounded flex-shrink-0">
                    {row.category || "-"}
                  </span>
                  {row.discipline_display && (
                    <span className="text-[9px] text-gray-500 truncate border-l pl-1 ml-0.5">
                      {row.discipline_display}
                    </span>
                  )}
                </div>

                {/* 제목 */}
                <h3 className="text-[13px] font-medium text-gray-900 truncate mb-1">
                  {row.title || "제목 없음"}
                </h3>

                {/* 하단: 작성자 + 날짜 */}
                <div className="flex items-center gap-1 text-[11px] text-gray-400">
                  <span className="truncate">
                    {row.created_by?.last_name || row.created_by?.first_name
                      ? `${row.created_by.last_name}${row.created_by.first_name}`
                      : row.created_by || "-"}
                  </span>
                  <span>·</span>
                  <span className="flex-shrink-0">
                    {row.created_at ? new Date(row.created_at).toLocaleDateString() : "-"}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        {!loading && !error && pagedRows.length > 0 && (
          <div className="bg-white rounded-lg p-3 border min-w-0">
            <MobileBoardPagination
              page={page}
              pageSize={pageSize}
              total={total}
              className="text-[11px] leading-tight gap-1 flex-wrap [&>span]:whitespace-nowrap [&>button]:px-2.5 [&>button]:py-1 [&>button]:text-[11px]"
              onPageChange={(p) => setPage(p)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
